/**
 * Where a "Fetch from MCA" click should go.
 *
 * The incident this exists to prevent: Add Company used to fetch MCA master
 * data for ANY typed CIN, fill the whole form, and save it with a blanket
 * `PUT /api/companies/:id`. For a CIN already in the portfolio that PUT carried
 * every form field — including the ones MCA does not publish (jurisdiction,
 * auditor, notes, contacts) which sat empty in the freshly-opened form — so real
 * data was silently blanked and registry fields were overwritten with no diff
 * shown. (Observed on ABBHANI FOODS / U10617RJ2024PTC092314: `jurisdiction`
 * blanked, `paidUpCapital` replaced.)
 *
 * So the routing decision is: an identifier we already store is a REFRESH
 * (server-side `POST /api/companies/:id/mca-refresh`, field-whitelisted and
 * skip-absent), never a client-side fetch-and-overwrite. An identifier we do not
 * store is a CREATE, where filling the form from the registry is exactly right
 * because there is nothing to destroy.
 *
 * Kept out of the component so the decision is unit-testable on its own.
 */

/** Access levels the mca-refresh endpoint accepts (mirrors the filings PATCH). */
const WRITE_LEVELS = ['read_write', 'admin'];

/**
 * @param {string} identifier CIN / LLPIN / FCRN as typed.
 * @param {Array<object>} companies The user's portfolio (from AppContext).
 * @returns {{ mode: 'empty'|'create'|'refresh'|'blocked', query: string,
 *   company: object|null, companyId: string|null, reason?: string }}
 *   - `empty`    nothing typed.
 *   - `create`   not in the portfolio → fetch and fill the form, then create.
 *   - `refresh`  already in the portfolio → route through refreshFromMca(companyId).
 *   - `blocked`  in the portfolio but we must not write: read-only share, or the
 *                cached row carries no id to address the refresh to.
 */
export function resolveMcaFetchRoute(identifier, companies) {
  const query = String(identifier || '').trim().toUpperCase();
  if (!query) return { mode: 'empty', query: '', company: null, companyId: null };

  const match = (Array.isArray(companies) ? companies : []).find(
    (c) => String(c?.cin || '').trim().toUpperCase() === query,
  ) || null;

  if (!match) return { mode: 'create', query, company: null, companyId: null };

  const companyId = match._id || match.id || null;
  if (!companyId) {
    // We know this CIN is ours but can't address the refresh endpoint. Falling
    // back to the fetch-and-fill path would re-open the overwrite hole, so stop.
    return { mode: 'blocked', query, company: match, companyId: null, reason: 'no-id' };
  }

  const level = match.myAccessLevel;
  // Unknown level (older cached list) errs on allowing the click — the server
  // stays the enforcement point, exactly as on the Annual Filing page.
  if (level != null && !WRITE_LEVELS.includes(level)) {
    return { mode: 'blocked', query, company: match, companyId, reason: 'read-only' };
  }

  return { mode: 'refresh', query, company: match, companyId };
}

/**
 * Field name → human label for `notPublished`, which the endpoint returns as raw
 * schema field names. Mirrors the `label`s in MCA_APPLY_FIELDS
 * (backend/controllers/companyController.js); an unmapped name falls through
 * verbatim rather than being dropped, so a whitelist addition is still reported.
 */
const FIELD_LABELS = {
  name: 'Name',
  status: 'Status',
  roc: 'ROC',
  category: 'Category',
  subCategory: 'Sub-category',
  classOfCompany: 'Class of company',
  registrationNo: 'Registration number',
  registeredAddress: 'Registered address',
  jurisdiction: 'State',
  listed: 'Listing status',
  authorisedCapital: 'Authorised capital',
  paidUpCapital: 'Paid-up capital',
  incorporated: 'Date of incorporation',
  balanceSheetDate: 'Balance sheet date',
};

/**
 * Turn the mca-refresh response into the sentence we show the user.
 *
 * Names the fields that actually changed rather than claiming a vague "updated
 * from MCA", and states the as-of date so a stale fetch is never read as
 * current truth. `notPublished` is surfaced because "MCA doesn't publish this"
 * is the honest reason a field the user expected to move didn't.
 *
 * @param {{ applied?: Array<{field?: string, label?: string}>,
 *   notPublished?: string[], mcaDataAsOf?: string }} response
 * @param {(d: string) => string} [formatDate] Date formatter (formatDateEN).
 */
export function summarizeMcaRefresh(response, formatDate) {
  const applied = Array.isArray(response?.applied) ? response.applied : [];
  const notPublished = Array.isArray(response?.notPublished) ? response.notPublished : [];
  const fmt = typeof formatDate === 'function' ? formatDate : (v) => String(v);
  const when = response?.mcaDataAsOf ? fmt(response.mcaDataAsOf) : '';

  const parts = [];
  if (applied.length) {
    const names = applied.map((a) => a?.label || a?.field).filter(Boolean).join(', ');
    parts.push(`Updated from MCA${when ? ` (as of ${when})` : ''}: ${names}.`);
  } else {
    parts.push(`Already matches MCA master data${when ? ` (checked ${when})` : ''}.`);
  }
  if (notPublished.length) {
    const names = notPublished.map((f) => FIELD_LABELS[f] || f).join(', ');
    parts.push(`Not published by MCA, so left as they were: ${names}.`);
  }
  parts.push('Your manual entries were not touched.');
  return parts.join(' ');
}

export default resolveMcaFetchRoute;
